// web/src/pages/Inventory/InventoryPro.jsx
import React, { useMemo, useState, useEffect } from "react";
import {
  Box, Paper, Typography, Stack, Button, Chip, Divider, TextField,
  Drawer, Tabs, Tab, IconButton, Dialog, DialogTitle, DialogContent, DialogActions,
  MenuItem, CircularProgress, Tooltip, Snackbar, Alert, Grid, Menu,
  ListItemIcon, ListItemText
} from "@mui/material";
import { BrandConfirmDialog } from "../modals/wizard/BrandDialogs";
import { DataGrid } from "@mui/x-data-grid";
import {
  Add, Edit, Delete, Close, Visibility, DownloadRounded,
  RefreshRounded, MoreHorizRounded, SearchRounded, Inventory2Outlined,
  WarningAmberRounded, InsightsOutlined, ArrowForwardRounded
} from "@mui/icons-material";
import { useTheme, alpha } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";

import {
  useListItemsQuery,
  useCreateItemMutation,
  useDeleteItemMutation,
  useStockInMutation,
  useListItemUnitsQuery,
  useListItemTxnsByItemQuery,
  useLazySearchOwnersQuery,
} from "../../redux/admin/inventoryApi";

const CATEGORY_OPTIONS = [
  { value: "gps", label: "GPS Tracker" },
  { value: "sim", label: "SIM Card" },
  { value: "fastag", label: "FASTag" },
  { value: "tyre", label: "Tyre" },
  { value: "battery", label: "Battery" },
  { value: "sensor", label: "Sensor" },
  { value: "tool", label: "Tool / Accessory" },
  { value: "general", label: "General Item" },
];
const CATEGORY_LABELS = Object.fromEntries(CATEGORY_OPTIONS.map((option) => [option.value, option.label]));
const OWNER_TYPES = [
  { value: "warehouse", label: "Warehouse", description: "Central stock. Kisi ko assign nahi hoga." },
  { value: "dealer", label: "Dealer / Branch", description: "Dealer ya branch-level stock handover." },
  { value: "employee", label: "Employee / Staff", description: "Field, office, HR ya manager staff ko issue karo." },
  { value: "user", label: "Customer / User", description: "End customer account ko assign karo." },
  { value: "device", label: "Device Record", description: "Tracker/device record se directly link karo." },
  { value: "vehicle", label: "Vehicle", description: "Vehicle installation ya vehicle-wise inventory tracking." },
];

function getCounts(it) {
  const row = it || {};
  const units = Array.isArray(row.units) ? row.units : [];
  const assignedCount = row.isSerialized
    ? units.filter(u => u?.status === "assigned").length
    : units
        .filter((u) => u?.status === "assigned")
        .reduce((sum, unit) => sum + Math.max(1, Number(unit?.quantity || 1)), 0);

  const inStock = row.isSerialized
    ? units.filter(u => u?.status === "in_stock").length
    : Number(row.quantityOnHand || 0);

  return { assignedCount, inStock };
}

function formatOwnerDisplay(owner = {}) {
  const label = String(owner?.ownerLabel || "").trim() || "Warehouse";
  const type = String(owner?.ownerType || "").trim();
  if (!type || type === "warehouse") return label;
  const typeLabel = OWNER_TYPES.find((option) => option.value === type)?.label || type;
  return `${label} • ${typeLabel}`;
}

// ✅ DataGrid safe helper
const safeRow = (params) => params?.row || {};
const isGpsCategory = (category) => String(category || "").toLowerCase() === "gps";
const isSimCategory = (category) => String(category || "").toLowerCase() === "sim";
const isElectronicsCategory = (category) => isGpsCategory(category) || isSimCategory(category) || String(category || "").toLowerCase() === "sensor";
const getCategoryLabel = (category) => CATEGORY_LABELS[String(category || "general").toLowerCase()] || "General Item";

export default function InventoryPro({ embedded = false }) {
  const theme = useTheme();
  const isSmDown = useMediaQuery(theme.breakpoints.down("sm"));
  const [q, setQ] = useState("");
  const { data, isLoading, isFetching, refetch } = useListItemsQuery({ q });
  const [lastCreatedItemId, setLastCreatedItemId] = useState("");
  const [lastCreatedItemName, setLastCreatedItemName] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [typeFilter, setTypeFilter] = useState("all");
  const [stockFilter, setStockFilter] = useState("all");
  const [qaQty, setQaQty] = useState(1);
  const [actionAnchor, setActionAnchor] = useState(null);
  const [actionItem, setActionItem] = useState(null);

  const items = useMemo(() => (Array.isArray(data?.items) ? data.items : []), [data?.items]);

  const [createItem, { isLoading: creating }] = useCreateItemMutation();
  const [deleteItem, { isLoading: deleting }] = useDeleteItemMutation();
  const [stockIn, { isLoading: stocking }] = useStockInMutation();

  const busy = creating || deleting || stocking;

  // dialogs
  const [dialog, setDialog] = useState(null); // {type, item}
  const closeDialog = () => setDialog(null);

  // drawer
  const [selected, setSelected] = useState(null);
  const [tab, setTab] = useState(0);

  // toast
  const [toast, setToast] = useState({ open: false, type: "success", msg: "" });
  const showToast = (type, msg) => setToast({ open: true, type, msg });

  // quick add
  const [qaName, setQaName] = useState("");
  const [qaCategory, setQaCategory] = useState("general");
  const [qaSerialized, setQaSerialized] = useState(false);
  const [qaExtra, setQaExtra] = useState({
    imei: "",
    modelType: "",
    simNumber: "",
    imsiNumber: "",
    provider: "",
    simType: "",
    iotSimNumber: "",
  });

  const stats = useMemo(() => {
    const total = items.length;
    const low = items.filter((i) => !i?.isSerialized && (i?.quantityOnHand || 0) <= (i?.minStock || 0) && (i?.quantityOnHand || 0) > 0).length;
    const out = items.filter((i) => getCounts(i).inStock <= 0).length;
    const inStock = Math.max(0, total - low - out);
    return { total, low, out, inStock };
  }, [items]);

  const filteredItems = useMemo(() => {
    return items.filter((item) => {
      const counts = getCounts(item);
      const status =
        counts.inStock <= 0 ? "out" :
        (!item?.isSerialized && counts.inStock <= Number(item?.minStock || 0)) ? "low" :
        "in";

      if (categoryFilter !== "all" && String(item?.category || "") !== categoryFilter) return false;
      if (typeFilter !== "all" && (item?.isSerialized ? "serialized" : "bulk") !== typeFilter) return false;
      if (stockFilter !== "all" && status !== stockFilter) return false;
      return true;
    });
  }, [items, categoryFilter, typeFilter, stockFilter]);

  const columns = useMemo(() => ([
    {
      field: "name",
      headerName: "Item Details",
      flex: 1,
      minWidth: 240,
      renderCell: (params) => (
        <Stack spacing={0.4} sx={{ py: 0.8 }}>
          <Typography sx={{ fontWeight: 900, color: "#0f172a" }}>{params?.row?.name || "-"}</Typography>
          <Typography variant="caption" color="text.secondary">SKU: {params?.row?.sku || "-"}</Typography>
        </Stack>
      ),
    },

    {
      field: "category",
      headerName: "Category",
      width: 140,
      valueGetter: (params) => {
        const row = safeRow(params);
        return getCategoryLabel(row.category);
      },
    },

    {
      field: "type",
      headerName: "Type",
      width: 130,
      renderCell: (params) => {
        const row = safeRow(params);
        return <Chip size="small" label={row.isSerialized ? "Serialized" : "Bulk"} color={row.isSerialized ? "secondary" : "default"} />;
      },
    },
    {
      field: "unit",
      headerName: "Unit",
      width: 100,
      valueGetter: (params) => safeRow(params)?.unit || "pcs",
    },

    {
      field: "inStock",
      headerName: "In Stock",
      width: 120,
      valueGetter: (params) => getCounts(safeRow(params)).inStock,
    },

    {
      field: "assigned",
      headerName: "Assigned",
      width: 120,
      valueGetter: (params) => getCounts(safeRow(params)).assignedCount,
    },
    {
      field: "available",
      headerName: "Available",
      width: 120,
      valueGetter: (params) => {
        const counts = getCounts(safeRow(params));
        return Math.max(0, counts.inStock - counts.assignedCount);
      },
    },
    {
      field: "actions",
      headerName: "Actions",
      width: 190,
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        <Stack direction="row" spacing={0.5}>
          <Tooltip title="Open inventory item">
            <IconButton
              size="small"
              color="primary"
              onClick={(event) => {
                event.stopPropagation();
                if (!params?.row?._id) return;
                setSelected(params.row);
                setTab(0);
              }}
            >
              <Visibility fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Stock in">
            <span>
              <IconButton
                size="small"
                color="success"
                disabled={busy}
                onClick={(event) => {
                  event.stopPropagation();
                  if (!params?.row?._id) return;
                  setSelected(params.row);
                  setTab(0);
                  setDialog({ type: "stockin", item: params.row });
                }}
              >
                <Add fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title="More actions">
            <IconButton
              size="small"
              onClick={(event) => {
                event.stopPropagation();
                setActionAnchor(event.currentTarget);
                setActionItem(params.row);
              }}
            >
              <MoreHorizRounded fontSize="small" />
            </IconButton>
          </Tooltip>
        </Stack>
      ),
    },
  ]), [busy]);

  const onCreate = async () => {
    const name = qaName.trim();
    if (!name) return showToast("warning", "Name required");
    if (qaCategory === "gps" && !qaSerialized) {
      return showToast("warning", "GPS inventory item serialized hona chahiye.");
    }
    if (isGpsCategory(qaCategory) && !qaExtra.imei.trim()) {
      return showToast("warning", "GPS ke liye IMEI required hai.");
    }
    if (isGpsCategory(qaCategory) && !qaExtra.modelType.trim()) {
      return showToast("warning", "GPS ke liye model type required hai.");
    }
    if (isSimCategory(qaCategory) && !qaExtra.simNumber.trim()) {
      return showToast("warning", "SIM ke liye sim number required hai.");
    }
    if (isSimCategory(qaCategory) && !qaExtra.imsiNumber.trim()) {
      return showToast("warning", "SIM ke liye IMSI number required hai.");
    }
    if (isSimCategory(qaCategory) && !qaExtra.provider.trim()) {
      return showToast("warning", "SIM ke liye provider required hai.");
    }
    if (isSimCategory(qaCategory) && !qaExtra.simType.trim()) {
      return showToast("warning", "SIM ke liye sim type required hai.");
    }
    if (isSimCategory(qaCategory) && !qaExtra.iotSimNumber.trim()) {
      return showToast("warning", "SIM ke liye actual IoT sim number required hai.");
    }

    try {
      const response = await createItem({
        name,
        category: qaCategory,
        itemType: qaCategory,
        isSerialized: qaSerialized,
        unit: "pcs",
        minStock: 0,
        quantityOnHand: qaSerialized ? 0 : Math.max(1, Number(qaQty || 1)),
        modelType: qaExtra.modelType,
        simNumber: qaExtra.simNumber,
        imsiNumber: qaExtra.imsiNumber,
        provider: qaExtra.provider,
        simType: qaExtra.simType,
        iotSimNumber: qaExtra.iotSimNumber,
        units: qaSerialized && (isGpsCategory(qaCategory) || isSimCategory(qaCategory))
          ? [{
              serial: isGpsCategory(qaCategory) ? qaExtra.imei : qaExtra.simNumber,
              imei: qaExtra.imei,
              modelType: qaExtra.modelType,
              simNumber: qaExtra.simNumber,
              imsiNumber: qaExtra.imsiNumber,
              provider: qaExtra.provider,
              simType: qaExtra.simType,
              iotSimNumber: qaExtra.iotSimNumber,
            }]
          : [],
      }).unwrap();

      setQaName("");
      setQaCategory("general");
      setQaSerialized(false);
      setQaQty(1);
      setQaExtra({
        imei: "",
        modelType: "",
        simNumber: "",
        imsiNumber: "",
        provider: "",
        simType: "",
        iotSimNumber: "",
      });
      setQ("");
      setLastCreatedItemId(String(response?.item?._id || ""));
      setLastCreatedItemName(String(response?.item?.name || name));
      if (response?.item?._id) {
        setSelected(response.item);
        setTab(0);
      }
      refetch();
      showToast("success", "Inventory item added. It is visible in the list below.");
    } catch (e) {
      showToast("error", e?.data?.msg || e?.message || "Create failed");
    }
  };

  const onDelete = async (item) => {
    try {
      await deleteItem(item._id).unwrap();
      showToast("success", "Item deleted");
      closeDialog();
      if (selected?._id === item._id) setSelected(null);
    } catch (e) {
      showToast("error", e?.data?.msg || e?.message || "Delete failed");
    }
  };

  const syncSelectedItem = (updatedItem) => {
    if (!updatedItem?._id) return;
    setSelected((prev) => (prev?._id === updatedItem._id ? updatedItem : prev));
  };

  const resetFilters = () => {
    setQ("");
    setCategoryFilter("all");
    setTypeFilter("all");
    setStockFilter("all");
  };

  const handleExport = () => {
    const header = ["Name", "Category", "Type", "Unit", "In Stock", "Assigned", "Available", "SKU"];
    const rows = filteredItems.map((item) => {
      const counts = getCounts(item);
      return [
        item?.name || "",
        getCategoryLabel(item?.category),
        item?.isSerialized ? "Serialized" : "Bulk",
        item?.unit || "pcs",
        counts.inStock,
        counts.assignedCount,
        Math.max(0, counts.inStock - counts.assignedCount),
        item?.sku || "",
      ];
    });
    const csv = [header, ...rows]
      .map((row) => row.map((cell) => `"${String(cell ?? "").replace(/"/g, '""')}"`).join(","))
      .join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = "inventory-items.csv";
    link.click();
    URL.revokeObjectURL(url);
  };

  return (
    <Box sx={{ p: embedded ? 0 : 2 }}>
      {!embedded ? (
        <Stack direction={{ xs: "column", md: "row" }} justifyContent="space-between" alignItems={{ xs: "flex-start", md: "center" }} sx={{ mb: 2 }} spacing={1.5}>
          <Box>
            <Typography variant="h4" fontWeight={900}>Inventory Management</Typography>
            <Typography sx={{ color: "#64748b", mt: 0.4 }}>Manage inventory items, stock and transactions</Typography>
          </Box>
          <Button variant="contained" startIcon={<Add />} sx={{ borderRadius: 3, px: 2.8, fontWeight: 800 }} onClick={() => document.getElementById("inventory-quick-add-name")?.focus()}>
            Add Inventory
          </Button>
        </Stack>
      ) : null}

      <Stack direction={{ xs: "column", xl: "row" }} spacing={2} alignItems="stretch">
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item xs={12} md={6}>
              <Paper variant="outlined" sx={{ p: 2.2, borderRadius: 4, borderColor: alpha(theme.palette.divider, 0.9), boxShadow: "0 18px 40px rgba(15,23,42,0.05)" }}>
                <Stack direction="row" spacing={2} alignItems="center">
                  <Box sx={{ width: 54, height: 54, borderRadius: 3, bgcolor: alpha(theme.palette.primary.main, 0.1), color: theme.palette.primary.main, display: "grid", placeItems: "center" }}>
                    <Inventory2Outlined />
                  </Box>
                  <Box>
                    <Typography sx={{ color: "#64748b", fontSize: 14 }}>Total Items</Typography>
                    <Typography sx={{ fontSize: 28, fontWeight: 900, color: "#0f172a" }}>{stats.total}</Typography>
                  </Box>
                </Stack>
              </Paper>
            </Grid>
            <Grid item xs={12} md={6}>
              <Paper variant="outlined" sx={{ p: 2.2, borderRadius: 4, borderColor: alpha(theme.palette.divider, 0.9), boxShadow: "0 18px 40px rgba(15,23,42,0.05)" }}>
                <Stack direction="row" spacing={2} alignItems="center">
                  <Box sx={{ width: 54, height: 54, borderRadius: 3, bgcolor: alpha(theme.palette.warning.main, 0.12), color: theme.palette.warning.main, display: "grid", placeItems: "center" }}>
                    <WarningAmberRounded />
                  </Box>
                  <Box>
                    <Typography sx={{ color: "#64748b", fontSize: 14 }}>Low Stock Items</Typography>
                    <Typography sx={{ fontSize: 28, fontWeight: 900, color: "#0f172a" }}>{stats.low}</Typography>
                  </Box>
                </Stack>
              </Paper>
            </Grid>
          </Grid>

          <Paper variant="outlined" sx={{ p: { xs: 1.6, md: 2.2 }, mb: 2, borderRadius: 4, borderColor: alpha(theme.palette.divider, 0.9), boxShadow: "0 18px 40px rgba(15,23,42,0.05)" }}>
            <Typography fontWeight={900} sx={{ mb: 1.6, color: "#0f172a", fontSize: 20 }}>Quick Add Item</Typography>
            <Grid container spacing={1.4}>
              <Grid item xs={12} md={3}>
                <TextField
                  id="inventory-quick-add-name"
                  fullWidth
                  value={qaName}
                  onChange={(e) => setQaName(e.target.value)}
                  placeholder="Enter item name"
                  disabled={busy}
                  onKeyDown={(e) => e.key === "Enter" && onCreate()}
                />
              </Grid>
              <Grid item xs={12} md={2}>
                <TextField
                  select
                  fullWidth
                  value={qaCategory}
                  onChange={(e) => {
                    const nextCategory = e.target.value;
                    setQaCategory(nextCategory);
                    setQaExtra({
                      imei: "",
                      modelType: "",
                      simNumber: "",
                      imsiNumber: "",
                      provider: "",
                      simType: "",
                      iotSimNumber: "",
                    });
                    if (nextCategory === "gps" || nextCategory === "sensor" || nextCategory === "sim") {
                      setQaSerialized(true);
                    }
                  }}
                  disabled={busy}
                >
                  {CATEGORY_OPTIONS.map((option) => (
                    <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={12} md={2}>
                <TextField
                  select
                  fullWidth
                  value={qaSerialized ? "yes" : "no"}
                  onChange={(e) => {
                    const nextSerialized = e.target.value === "yes";
                    if ((qaCategory === "gps" || qaCategory === "sensor" || qaCategory === "sim") && !nextSerialized) {
                      showToast("warning", `${qaCategory.toUpperCase()} items serialized hi rahenge.`);
                      return;
                    }
                    setQaSerialized(nextSerialized);
                  }}
                  disabled={busy}
                >
                  <MenuItem value="yes">Serialized</MenuItem>
                  <MenuItem value="no">Bulk</MenuItem>
                </TextField>
              </Grid>
              <Grid item xs={12} md={2}>
                <TextField fullWidth value="pcs" disabled />
              </Grid>
              <Grid item xs={12} md={1}>
                <TextField fullWidth type="number" value={qaQty} onChange={(e) => setQaQty(e.target.value)} inputProps={{ min: 1 }} disabled={busy || qaSerialized} />
              </Grid>
              <Grid item xs={12} md={2}>
                <Button variant="contained" onClick={onCreate} disabled={busy} startIcon={<Add />} sx={{ minHeight: 56, width: "100%", borderRadius: 3, fontWeight: 800 }}>
                  {creating ? <CircularProgress size={18} /> : "Add Item"}
                </Button>
              </Grid>
            </Grid>

            {isGpsCategory(qaCategory) && (
              <Stack direction={{ xs: "column", md: "row" }} spacing={1} sx={{ mt: 1.5 }}>
                <TextField fullWidth label="IMEI Number *" value={qaExtra.imei} onChange={(e) => setQaExtra((prev) => ({ ...prev, imei: e.target.value.replace(/\D/g, "").slice(0, 15) }))} />
                <TextField fullWidth label="Model Type *" value={qaExtra.modelType} onChange={(e) => setQaExtra((prev) => ({ ...prev, modelType: e.target.value }))} />
              </Stack>
            )}

            {isSimCategory(qaCategory) && (
              <Grid container spacing={1.2} sx={{ mt: 0.4 }}>
                <Grid item xs={12} md={4}><TextField fullWidth label="SIM Number *" value={qaExtra.simNumber} onChange={(e) => setQaExtra((prev) => ({ ...prev, simNumber: e.target.value }))} /></Grid>
                <Grid item xs={12} md={4}><TextField fullWidth label="IMSI Number *" value={qaExtra.imsiNumber} onChange={(e) => setQaExtra((prev) => ({ ...prev, imsiNumber: e.target.value }))} /></Grid>
                <Grid item xs={12} md={4}><TextField fullWidth label="Provider *" value={qaExtra.provider} onChange={(e) => setQaExtra((prev) => ({ ...prev, provider: e.target.value }))} /></Grid>
                <Grid item xs={12} md={4}><TextField fullWidth label="SIM Type *" value={qaExtra.simType} onChange={(e) => setQaExtra((prev) => ({ ...prev, simType: e.target.value }))} /></Grid>
                <Grid item xs={12} md={8}><TextField fullWidth label="Actual IoT SIM Number *" value={qaExtra.iotSimNumber} onChange={(e) => setQaExtra((prev) => ({ ...prev, iotSimNumber: e.target.value }))} /></Grid>
              </Grid>
            )}

            <Grid container spacing={1.5} sx={{ mt: 0.8 }}>
              <Grid item xs={12} md={6}>
                <Alert severity="info" sx={{ borderRadius: 3 }}>Category select karne se inventory ka behavior change hota hai. GPS Tracker aur SIM Card me serial-specific fields aayengi, jabki General Item bulk stock ke liye best hai.</Alert>
              </Grid>
              <Grid item xs={12} md={6}>
                <Alert severity="success" sx={{ borderRadius: 3 }}>Inventory add karne ke baad item seedha neeche list me aa jata hai. Yahan stock maintain karo, aur field employee ko actual material task flow ke through do.</Alert>
              </Grid>
            </Grid>
          </Paper>

          <Paper variant="outlined" sx={{ p: { xs: 1.6, md: 2.2 }, mb: 2, borderRadius: 4, borderColor: alpha(theme.palette.divider, 0.9), boxShadow: "0 18px 40px rgba(15,23,42,0.05)" }}>
            <Stack direction={{ xs: "column", lg: "row" }} spacing={1.2}>
              <TextField fullWidth value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by name, SKU, serial, IMEI..." InputProps={{ endAdornment: <SearchRounded color="action" /> }} />
              <TextField select value={categoryFilter} onChange={(e) => setCategoryFilter(e.target.value)} sx={{ minWidth: 170 }}>
                <MenuItem value="all">Category: All</MenuItem>
                {CATEGORY_OPTIONS.map((option) => (
                  <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                ))}
              </TextField>
              <TextField select value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)} sx={{ minWidth: 150 }}>
                <MenuItem value="all">Type: All</MenuItem>
                <MenuItem value="bulk">Bulk</MenuItem>
                <MenuItem value="serialized">Serialized</MenuItem>
              </TextField>
              <TextField select value={stockFilter} onChange={(e) => setStockFilter(e.target.value)} sx={{ minWidth: 170 }}>
                <MenuItem value="all">Stock Status: All</MenuItem>
                <MenuItem value="in">In Stock</MenuItem>
                <MenuItem value="low">Low Stock</MenuItem>
                <MenuItem value="out">Out of Stock</MenuItem>
              </TextField>
              <Button variant="outlined" onClick={resetFilters} startIcon={<RefreshRounded />} sx={{ borderRadius: 2.5, minWidth: 110 }}>Reset</Button>
              <Button variant="outlined" onClick={handleExport} startIcon={<DownloadRounded />} sx={{ borderRadius: 2.5, minWidth: 110 }}>Export</Button>
            </Stack>
          </Paper>

          <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 4, borderColor: alpha(theme.palette.divider, 0.9), boxShadow: "0 18px 40px rgba(15,23,42,0.05)" }}>
            <Stack direction={{ xs: "column", md: "row" }} alignItems={{ xs: "flex-start", md: "center" }} justifyContent="space-between" spacing={1} sx={{ px: 1, pb: 1 }}>
              <Box>
                <Typography fontWeight={900} sx={{ color: "#0f172a", fontSize: 20 }}>Inventory Items</Typography>
                <Typography variant="body2" color="text.secondary">
                  {filteredItems.length} item{filteredItems.length === 1 ? "" : "s"} visible
                  {q ? ` for "${q}"` : ""}.
                </Typography>
              </Box>
              <Stack direction="row" spacing={1} alignItems="center">
                {lastCreatedItemName ? <Chip color="success" variant="outlined" label={`Last added: ${lastCreatedItemName}`} /> : null}
                <Button variant="outlined" startIcon={<RefreshRounded />} onClick={() => refetch()} disabled={isFetching}>
                  {isFetching ? "Refreshing..." : "Refresh"}
                </Button>
              </Stack>
            </Stack>

            {filteredItems.length === 0 && !isLoading ? (
              <Box sx={{ height: 420, display: "flex", alignItems: "center", justifyContent: "center", textAlign: "center", px: 3 }}>
                <Stack spacing={1.2} alignItems="center">
                  <Typography variant="h6" fontWeight={800}>No inventory items visible</Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 560 }}>
                    Upar Quick Add se inventory create karo. Create hote hi item yahin list me dikhega. Search ya filters clear karke dobara check karo.
                  </Typography>
                </Stack>
              </Box>
            ) : isSmDown ? (
              <Stack spacing={1.2} sx={{ px: 0.4, pb: 0.6 }}>
                {filteredItems.map((item) => {
                  const counts = getCounts(item);
                  const isLow = !item?.isSerialized && counts.inStock <= Number(item?.minStock || 0) && counts.inStock > 0;
                  return (
                    <Paper key={item._id} variant="outlined" sx={{ p: 1.5, borderRadius: 3 }}>
                      <Stack spacing={1.1}>
                        <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                          <Box>
                            <Typography sx={{ fontWeight: 900 }}>{item.name}</Typography>
                            <Typography variant="body2" color="text.secondary">SKU: {item?.sku || "-"}</Typography>
                          </Box>
                          <IconButton size="small" onClick={(event) => { setActionAnchor(event.currentTarget); setActionItem(item); }}>
                            <MoreHorizRounded fontSize="small" />
                          </IconButton>
                        </Stack>
                        <Stack direction="row" spacing={1} flexWrap="wrap">
                          <Chip size="small" label={getCategoryLabel(item.category)} color="primary" variant="outlined" />
                          <Chip size="small" label={item.isSerialized ? "Serialized" : "Bulk"} />
                          <Chip size="small" label={counts.inStock <= 0 ? "Out of Stock" : isLow ? "Low Stock" : "In Stock"} color={counts.inStock <= 0 ? "error" : isLow ? "warning" : "success"} />
                        </Stack>
                        <Typography variant="body2" color="text.secondary">In Stock: {counts.inStock} • Assigned: {counts.assignedCount}</Typography>
                        <Typography variant="body2" color="text.secondary">Available: {Math.max(0, counts.inStock - counts.assignedCount)}</Typography>
                      </Stack>
                    </Paper>
                  );
                })}
              </Stack>
            ) : (
              <DataGrid
                autoHeight
                rows={filteredItems || []}
                columns={columns}
                getRowId={(r) => r?._id}
                loading={isLoading}
                onRowClick={(params) => {
                  if (!params?.row?._id) return;
                  setSelected(params.row);
                  setTab(0);
                }}
                pageSizeOptions={[10, 25, 50]}
                initialState={{ pagination: { paginationModel: { pageSize: 10, page: 0 } } }}
                getRowClassName={(params) => String(params?.row?._id || "") === lastCreatedItemId ? "inventory-new-row" : ""}
                sx={{
                  border: 0,
                  "& .inventory-new-row": {
                    backgroundColor: "rgba(16, 185, 129, 0.08)",
                  },
                  "& .MuiDataGrid-columnHeaders": {
                    backgroundColor: "#f8fafc",
                  },
                  "& .MuiDataGrid-cell": {
                    alignItems: "center",
                  },
                }}
              />
            )}
          </Paper>
        </Box>

        <Stack sx={{ width: { xs: "100%", xl: 300 }, minWidth: { xl: 300 } }} spacing={2}>
          <Paper variant="outlined" sx={{ p: 2.2, borderRadius: 4, borderColor: alpha(theme.palette.divider, 0.9), boxShadow: "0 18px 40px rgba(15,23,42,0.05)", position: { xl: "sticky" }, top: { xl: 20 } }}>
            <Typography sx={{ fontWeight: 900, color: "#0f172a", fontSize: 20, mb: 1.5 }}>Inventory Overview</Typography>
            <Box sx={{ display: "grid", placeItems: "center", py: 1 }}>
              <Box sx={{ width: 160, height: 160, borderRadius: "50%", background: `conic-gradient(${theme.palette.primary.main} 0 ${stats.total ? (stats.inStock / stats.total) * 360 : 0}deg, ${theme.palette.warning.main} 0 ${stats.total ? ((stats.inStock + stats.low) / stats.total) * 360 : 0}deg, ${theme.palette.error.main} 0 360deg)`, display: "grid", placeItems: "center" }}>
                <Box sx={{ width: 106, height: 106, borderRadius: "50%", bgcolor: "#fff", display: "grid", placeItems: "center", textAlign: "center" }}>
                  <Typography sx={{ fontWeight: 900, fontSize: 30 }}>{stats.total}</Typography>
                  <Typography sx={{ color: "#64748b", fontSize: 13 }}>Total Items</Typography>
                </Box>
              </Box>
            </Box>
            <Stack spacing={1.1} sx={{ mt: 2 }}>
              {[
                { label: "In Stock", value: stats.inStock, color: theme.palette.primary.main },
                { label: "Low Stock", value: stats.low, color: theme.palette.warning.main },
                { label: "Out of Stock", value: stats.out, color: theme.palette.error.main },
              ].map((row) => (
                <Stack key={row.label} direction="row" justifyContent="space-between" alignItems="center">
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Box sx={{ width: 10, height: 10, borderRadius: "50%", bgcolor: row.color }} />
                    <Typography sx={{ color: "#334155", fontSize: 14 }}>{row.label}</Typography>
                  </Stack>
                  <Typography sx={{ fontWeight: 800 }}>{row.value}</Typography>
                </Stack>
              ))}
            </Stack>
          </Paper>

          <Paper variant="outlined" sx={{ p: 2.2, borderRadius: 4, borderColor: alpha(theme.palette.divider, 0.9), boxShadow: "0 18px 40px rgba(15,23,42,0.05)" }}>
            <Typography sx={{ fontWeight: 900, color: "#0f172a", fontSize: 20, mb: 1.5 }}>Quick Actions</Typography>
            <Stack spacing={1}>
              {[
                { label: "Stock In", hint: "Add new stock", icon: <Add fontSize="small" />, onClick: () => selected && setDialog({ type: "stockin", item: selected }), disabled: !selected },
                { label: "Stock Out", hint: "Remove stock", icon: <Edit fontSize="small" />, disabled: true },
                { label: "View Transactions", hint: "All inventory transactions", icon: <InsightsOutlined fontSize="small" />, onClick: () => selected && setTab(2), disabled: !selected },
                { label: "Import Inventory", hint: "Bulk import items", icon: <DownloadRounded fontSize="small" />, disabled: true },
                { label: "Inventory Reports", hint: "View inventory reports", icon: <Inventory2Outlined fontSize="small" />, disabled: true },
              ].map((action) => (
                <Button key={action.label} variant="outlined" fullWidth disabled={action.disabled} onClick={action.onClick} sx={{ justifyContent: "space-between", borderRadius: 3, px: 1.4, py: 1.1, textTransform: "none" }}>
                  <Stack direction="row" spacing={1.2} alignItems="center">
                    {action.icon}
                    <Box sx={{ textAlign: "left" }}>
                      <Typography sx={{ fontWeight: 800, color: "#0f172a", fontSize: 14 }}>{action.label}</Typography>
                      <Typography sx={{ color: "#64748b", fontSize: 12 }}>{action.hint}</Typography>
                    </Box>
                  </Stack>
                  <ArrowForwardRounded fontSize="small" />
                </Button>
              ))}
            </Stack>
          </Paper>
        </Stack>
      </Stack>

      {/* Drawer */}
      <ItemDrawer
        open={!!selected}
        item={selected}
        tab={tab}
        setTab={setTab}
        onClose={() => setSelected(null)}
        busy={busy}
        onAction={(type) => setDialog({ type, item: selected })}
      />

      {/* Dialogs */}
      {dialog?.type === "stockin" && (
        <StockInDialog
          open
          item={dialog.item}
          busy={stocking}
          onClose={closeDialog}
          onSubmit={async (payload) => {
            try {
              const response = await stockIn({ id: dialog.item._id, ...payload }).unwrap();
              syncSelectedItem(response?.item);
              showToast("success", "Stock updated");
              closeDialog();
            } catch (e) {
              showToast("error", e?.data?.msg || e?.message || "Stock in failed");
            }
          }}
        />
      )}

      {dialog?.type === "delete" && (
        <ConfirmDeleteDialog
          open
          item={dialog.item}
          busy={deleting}
          onClose={closeDialog}
          onConfirm={() => onDelete(dialog.item)}
        />
      )}

      <Menu anchorEl={actionAnchor} open={Boolean(actionAnchor)} onClose={() => { setActionAnchor(null); setActionItem(null); }}>
        <MenuItem
          onClick={() => {
            if (actionItem) {
              setSelected(actionItem);
              setTab(0);
            }
            setActionAnchor(null);
            setActionItem(null);
          }}
        >
          <ListItemIcon><Visibility fontSize="small" /></ListItemIcon>
          <ListItemText>View Item</ListItemText>
        </MenuItem>
        <MenuItem
          onClick={() => {
            if (actionItem) {
              setSelected(actionItem);
              setTab(0);
              setDialog({ type: "stockin", item: actionItem });
            }
            setActionAnchor(null);
            setActionItem(null);
          }}
        >
          <ListItemIcon><Add fontSize="small" /></ListItemIcon>
          <ListItemText>Stock In</ListItemText>
        </MenuItem>
        <MenuItem
          onClick={() => {
            if (actionItem) {
              setSelected(actionItem);
              setDialog({ type: "delete", item: actionItem });
            }
            setActionAnchor(null);
            setActionItem(null);
          }}
          sx={{ color: theme.palette.error.main }}
        >
          <ListItemIcon><Delete fontSize="small" color="error" /></ListItemIcon>
          <ListItemText>Delete</ListItemText>
        </MenuItem>
      </Menu>

      {/* Toast */}
      <Snackbar
        open={toast.open}
        autoHideDuration={2500}
        onClose={() => setToast(t => ({ ...t, open: false }))}
      >
        <Alert
          severity={toast.type}
          variant="filled"
          onClose={() => setToast(t => ({ ...t, open: false }))}
        >
          {toast.msg}
        </Alert>
      </Snackbar>
    </Box>
  );
}

// ---------------- Drawer ----------------
function ItemDrawer({ open, item, tab, setTab, onClose, busy, onAction }) {
  const itemId = item?._id;

  const { data: unitsData, isLoading: unitsLoading } = useListItemUnitsQuery(
    { id: itemId },
    { skip: !itemId || tab !== 1 }
  );

  const { data: txnData, isLoading: txnLoading } = useListItemTxnsByItemQuery(
    { id: itemId },
    { skip: !itemId || tab !== 2 }
  );

  const units = unitsData?.units || [];
  const txns = txnData?.txns || [];

  return (
    <Drawer anchor="right" open={open} onClose={onClose} PaperProps={{ sx: { width: { xs: "100%", md: 520 }, background: "#f8fafc" } }}>
      <Box sx={{ p: 2 }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <Box>
            <Typography fontWeight={900} variant="h6">{item?.name || "-"}</Typography>
            <Typography variant="body2" color="text.secondary">
              {getCategoryLabel(item?.category)} • {item?.isSerialized ? "SERIALIZED" : "BULK"}
            </Typography>
          </Box>
          <IconButton onClick={onClose}><Close /></IconButton>
        </Stack>

        <Stack direction="row" spacing={1} sx={{ mt: 2, mb: 1.5, flexWrap: "wrap" }}>
          <Button startIcon={<Edit />} variant="contained" disabled={busy} onClick={() => onAction("stockin")} sx={{ borderRadius: 2.5, textTransform: "none", fontWeight: 800 }}>
            Stock In
          </Button>
          <Tooltip title="Delete item">
            <IconButton color="error" disabled={busy} onClick={() => onAction("delete")}>
              <Delete />
            </IconButton>
          </Tooltip>
        </Stack>

        <Paper variant="outlined" sx={{ borderRadius: 3, mb: 2, overflow: "hidden" }}>
        <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ px: 1 }}>
          <Tab label="Overview" />
          <Tab label="Units" />
          <Tab label="Transactions" />
        </Tabs>
        </Paper>

        {tab === 0 && <OverviewPanel item={item} />}
        {tab === 1 && <UnitsPanel item={item} units={units} loading={unitsLoading} />}
        {tab === 2 && <TxnPanel txns={txns} loading={txnLoading} />}
      </Box>
    </Drawer>
  );
}

function OverviewPanel({ item }) {
  const c = getCounts(item || {});
  return (
    <Paper sx={{ p: 2 }}>
      <Stack spacing={1}>
        <Typography fontWeight={800}>Counts</Typography>
        <Stack direction="row" spacing={1}>
          <Chip label={`In Stock: ${c.inStock}`} />
          <Chip label={`Assigned: ${c.assignedCount}`} color={c.assignedCount ? "info" : "default"} />
        </Stack>
        <Divider />
        <Typography variant="body2" color="text.secondary">SKU: {item?.sku || "-"}</Typography>
        <Typography variant="body2" color="text.secondary">Unit: {item?.unit || "pcs"}</Typography>
        <Typography variant="body2" color="text.secondary">Min Stock: {item?.minStock ?? 0}</Typography>
        {isGpsCategory(item?.category) && (
          <>
            <Typography variant="body2" color="text.secondary">Model Type: {item?.modelType || "-"}</Typography>
          </>
        )}
        {isSimCategory(item?.category) && (
          <>
            <Typography variant="body2" color="text.secondary">SIM Number: {item?.simNumber || "-"}</Typography>
            <Typography variant="body2" color="text.secondary">IMSI Number: {item?.imsiNumber || "-"}</Typography>
            <Typography variant="body2" color="text.secondary">Provider: {item?.provider || "-"}</Typography>
            <Typography variant="body2" color="text.secondary">SIM Type: {item?.simType || "-"}</Typography>
            <Typography variant="body2" color="text.secondary">Actual IoT SIM Number: {item?.iotSimNumber || "-"}</Typography>
          </>
        )}
      </Stack>
    </Paper>
  );
}

function UnitsPanel({ item, units, loading }) {
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("all");

  const filtered = useMemo(() => {
    const rx = q ? new RegExp(q, "i") : null;
    return (units || []).filter(u => {
      if (status !== "all" && u?.status !== status) return false;
      if (!rx) return true;
      return rx.test(u?.serial || "") || rx.test(u?.imei || "") || rx.test(u?.currentOwner?.ownerLabel || "");
    });
  }, [units, q, status]);

  const cols = [
    { field: "serial", headerName: "Serial", flex: 1, minWidth: 160 },
    { field: "imei", headerName: "IMEI", flex: 1, minWidth: 160 },
    ...(isGpsCategory(item?.category) ? [{ field: "modelType", headerName: "Model Type", flex: 1, minWidth: 150 }] : []),
    ...(isSimCategory(item?.category)
      ? [
          { field: "simNumber", headerName: "SIM Number", flex: 1, minWidth: 160 },
          { field: "imsiNumber", headerName: "IMSI Number", flex: 1, minWidth: 160 },
          { field: "provider", headerName: "Provider", flex: 1, minWidth: 140 },
          { field: "simType", headerName: "SIM Type", flex: 1, minWidth: 140 },
          { field: "iotSimNumber", headerName: "IoT SIM Number", flex: 1, minWidth: 170 },
        ]
      : []),
    { field: "status", headerName: "Status", width: 120 },
    {
      field: "owner",
      headerName: "Current Owner",
      flex: 1,
      minWidth: 200,
      valueGetter: (params) => formatOwnerDisplay(params?.row?.currentOwner),
    },
  ];

  if (!item?.isSerialized) {
    return (
      <Paper sx={{ p: 2 }}>
        <Typography>Bulk item hai. Units list applicable nahi. (Qty only)</Typography>
      </Paper>
    );
  }

  return (
    <Paper sx={{ p: 2 }}>
      <Stack direction={{ xs: "column", md: "row" }} spacing={1} sx={{ mb: 2 }}>
        <TextField fullWidth value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search serial/imei/owner..." />
        <TextField select value={status} onChange={(e) => setStatus(e.target.value)} sx={{ minWidth: 160 }}>
          <MenuItem value="all">All</MenuItem>
          <MenuItem value="in_stock">In Stock</MenuItem>
          <MenuItem value="assigned">Assigned</MenuItem>
          <MenuItem value="maintenance">Maintenance</MenuItem>
          <MenuItem value="lost">Lost</MenuItem>
        </TextField>
      </Stack>

      <Box sx={{ height: 380 }}>
        <DataGrid
          rows={filtered || []}
          columns={cols}
          getRowId={(r) => r?._id}
          loading={loading}
          pageSizeOptions={[10, 25, 50]}
          initialState={{ pagination: { paginationModel: { pageSize: 10, page: 0 } } }}
        />
      </Box>
    </Paper>
  );
}

function TxnPanel({ txns, loading }) {
  const cols = [
    {
      field: "createdAt",
      headerName: "Time",
      width: 200,
      valueGetter: (params) => {
        const v = params?.row?.createdAt;
        if (!v) return "-";
        try { return new Date(v).toLocaleString(); } catch { return String(v); }
      }
    },
    { field: "txnType", headerName: "Type", width: 120 },
    { field: "serial", headerName: "Serial", width: 180 },
    { field: "from", headerName: "From", flex: 1, minWidth: 200, valueGetter: (p) => formatOwnerDisplay(p?.row?.from) },
    { field: "to", headerName: "To", flex: 1, minWidth: 200, valueGetter: (p) => formatOwnerDisplay(p?.row?.to) },
    { field: "notes", headerName: "Notes", flex: 1, minWidth: 220 },
  ];

  return (
    <Paper sx={{ p: 2 }}>
      <Box sx={{ height: 420 }}>
        <DataGrid
          rows={txns || []}
          columns={cols}
          getRowId={(r) => r?._id}
          loading={loading}
          pageSizeOptions={[10, 25, 50]}
          initialState={{ pagination: { paginationModel: { pageSize: 10, page: 0 } } }}
        />
      </Box>
    </Paper>
  );
}

// ---------------- Dialogs ----------------
function StockInDialog({ open, item, busy, onClose, onSubmit }) {
  const [qty, setQty] = useState(1);
  const [serialsText, setSerialsText] = useState("");
  const [extra, setExtra] = useState({
    imei: "",
    modelType: "",
    simNumber: "",
    imsiNumber: "",
    provider: "",
    simType: "",
    iotSimNumber: "",
  });

  const parse = (txt) =>
    String(txt || "")
      .split(/[\n, ]+/g)
      .map((x) => x.trim())
      .filter(Boolean);

  const save = () => {
    if (item?.isSerialized) {
      if (isGpsCategory(item?.category)) {
        if (!extra.imei.trim() || !extra.modelType.trim()) return;
        return onSubmit({
          units: [{
            serial: extra.imei,
            imei: extra.imei,
            modelType: extra.modelType,
          }],
        });
      }
      if (isSimCategory(item?.category)) {
        if (!extra.simNumber.trim() || !extra.imsiNumber.trim() || !extra.provider.trim() || !extra.simType.trim() || !extra.iotSimNumber.trim()) return;
        return onSubmit({
          units: [{
            serial: extra.simNumber,
            simNumber: extra.simNumber,
            imsiNumber: extra.imsiNumber,
            provider: extra.provider,
            simType: extra.simType,
            iotSimNumber: extra.iotSimNumber,
          }],
        });
      }
      const serials = parse(serialsText);
      if (!serials.length) return;
      onSubmit({ units: serials.map(s => ({ serial: s })) });
    } else {
      onSubmit({ qty: Math.max(1, Number(qty || 1)) });
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth>
      <DialogTitle>Stock In: {item?.name || "-"}</DialogTitle>
      <DialogContent dividers>
        {item?.isSerialized ? (
          isGpsCategory(item?.category) ? (
            <Stack spacing={2}>
              <TextField
                fullWidth
                label="IMEI Number *"
                value={extra.imei}
                onChange={(e) => setExtra((prev) => ({ ...prev, imei: e.target.value.replace(/\D/g, "").slice(0, 15) }))}
              />
              <TextField
                fullWidth
                label="Model Type *"
                value={extra.modelType}
                onChange={(e) => setExtra((prev) => ({ ...prev, modelType: e.target.value }))}
              />
            </Stack>
          ) : isSimCategory(item?.category) ? (
            <Stack spacing={2}>
              <TextField fullWidth label="SIM Number *" value={extra.simNumber} onChange={(e) => setExtra((prev) => ({ ...prev, simNumber: e.target.value }))} />
              <TextField fullWidth label="IMSI Number *" value={extra.imsiNumber} onChange={(e) => setExtra((prev) => ({ ...prev, imsiNumber: e.target.value }))} />
              <TextField fullWidth label="Provider *" value={extra.provider} onChange={(e) => setExtra((prev) => ({ ...prev, provider: e.target.value }))} />
              <TextField fullWidth label="SIM Type *" value={extra.simType} onChange={(e) => setExtra((prev) => ({ ...prev, simType: e.target.value }))} />
              <TextField fullWidth label="Actual IoT SIM Number *" value={extra.iotSimNumber} onChange={(e) => setExtra((prev) => ({ ...prev, iotSimNumber: e.target.value }))} />
            </Stack>
          ) : (
            <TextField
              fullWidth
              multiline
              minRows={4}
              label="Serial/IMEI (paste multiple)"
              value={serialsText}
              onChange={(e) => setSerialsText(e.target.value)}
              placeholder={"Example:\n868000111222333\n868000111222334\nSN-12345"}
            />
          )
        ) : (
          <TextField fullWidth type="number" label="Qty" value={qty} onChange={(e) => setQty(e.target.value)} />
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={busy}>Cancel</Button>
        <Button variant="contained" onClick={save} disabled={busy}>
          {busy ? <CircularProgress size={18} /> : "Save"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function AssignTransferDialog({ open, mode, item, busy, onClose, onSubmit }) {
  const isTransfer = mode === "transfer";
  const isSerialized = !!item?.isSerialized;
  const suggestedOwnerType = isElectronicsCategory(item?.category) ? "vehicle" : "employee";

  const [serial, setSerial] = useState("");
  const [qty, setQty] = useState(1);
  const [ownerType, setOwnerType] = useState(suggestedOwnerType);
  const [selectedOwner, setSelectedOwner] = useState(null);
  const unitStatus = isTransfer ? "assigned" : "in_stock";

  const [triggerSearch, { data, isFetching }] = useLazySearchOwnersQuery();
  const { data: unitsData, isLoading: unitsLoading } = useListItemUnitsQuery(
    { id: item?._id, status: unitStatus },
    { skip: !open || !item?._id || !isSerialized }
  );
  const availableUnits = Array.isArray(unitsData?.units) ? unitsData.units : [];
  const bulkInStock = Math.max(0, Number(item?.quantityOnHand || 0));

  useEffect(() => {
    if (!open || ownerType === "warehouse") return;
    triggerSearch({ type: ownerType, q: "" });
  }, [open, ownerType, triggerSearch]);

  useEffect(() => {
    if (!open) return;
    setSerial("");
    setQty(1);
    setOwnerType(suggestedOwnerType);
    setSelectedOwner(null);
    triggerSearch({ type: suggestedOwnerType, q: "" });
  }, [open, suggestedOwnerType, triggerSearch]);

  const ownerTypeOptions = useMemo(() => {
    if (ownerType === "warehouse") return OWNER_TYPES;
    if (isElectronicsCategory(item?.category)) return OWNER_TYPES;
    return OWNER_TYPES.filter((option) => option.value !== "device");
  }, [item?.category, ownerType]);

  const selectedOwnerTypeMeta = useMemo(
    () => ownerTypeOptions.find((option) => option.value === ownerType) || OWNER_TYPES.find((option) => option.value === ownerType),
    [ownerType, ownerTypeOptions]
  );

  const owners = useMemo(() => {
    if (!data || data?.type !== ownerType) return [];
    return Array.isArray(data?.owners) ? data.owners : [];
  }, [data, ownerType]);

  const submitDisabled =
    busy ||
    (isSerialized ? !serial.trim() : Math.max(1, Number(qty || 1)) > bulkInStock) ||
    (ownerType !== "warehouse" && !selectedOwner?._id);

  const submit = () => {
    // ✅ validate
    if (isSerialized) {
      if (!serial.trim()) return;
    } else {
      const n = Math.max(1, Number(qty || 1));
      if (!n || n > bulkInStock) return;
    }

    // warehouse
    if (ownerType === "warehouse") {
      if (isTransfer) {
        return onSubmit(
          isSerialized
            ? { serial: serial.trim(), toOwnerType: "warehouse", toOwnerId: null, toOwnerLabel: "Warehouse" }
            : { qty: Math.max(1, Number(qty || 1)), toOwnerType: "warehouse", toOwnerId: null, toOwnerLabel: "Warehouse" }
        );
      }

      return onSubmit(
        isSerialized
          ? { ownerType: "warehouse", ownerId: null, ownerLabel: "Warehouse", serial: serial.trim() }
          : { ownerType: "warehouse", ownerId: null, ownerLabel: "Warehouse", qty: Math.max(1, Number(qty || 1)) }
      );
    }

    // vehicle (select from list or manual label)
    if (ownerType === "vehicle") {
      const label = selectedOwner?.meta?.vehicleNumber || selectedOwner?.label || "";
      if (!label) return;

      if (isTransfer) {
        return onSubmit(
          isSerialized
            ? { serial: serial.trim(), toOwnerType: "vehicle", toOwnerId: null, toOwnerLabel: label }
            : { qty: Math.max(1, Number(qty || 1)), toOwnerType: "vehicle", toOwnerId: null, toOwnerLabel: label }
        );
      }

      return onSubmit(
        isSerialized
          ? { ownerType: "vehicle", ownerId: null, ownerLabel: label, serial: serial.trim() }
          : { ownerType: "vehicle", ownerId: null, ownerLabel: label, qty: Math.max(1, Number(qty || 1)) }
      );
    }

    // other owners require selectedOwner
    if (!selectedOwner?._id) return;

    const label = selectedOwner.label;

    if (isTransfer) {
      return onSubmit(
        isSerialized
          ? { serial: serial.trim(), toOwnerType: ownerType, toOwnerId: selectedOwner._id, toOwnerLabel: label }
          : { qty: Math.max(1, Number(qty || 1)), toOwnerType: ownerType, toOwnerId: selectedOwner._id, toOwnerLabel: label }
      );
    }

    return onSubmit(
      isSerialized
        ? { ownerType, ownerId: selectedOwner._id, ownerLabel: label, serial: serial.trim() }
        : { ownerType, ownerId: selectedOwner._id, ownerLabel: label, qty: Math.max(1, Number(qty || 1)) }
    );
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth>
      <DialogTitle>{isTransfer ? "Transfer" : "Assign"}: {item?.name || "-"}</DialogTitle>

      <DialogContent dividers>
        <Stack spacing={2}>
          <Alert severity="info">
            {isTransfer
              ? "Step 1: assigned unit select karo. Step 2: jis owner ko dena hai wo dropdown se select karo."
              : "Step 1: available stock/unit select karo. Step 2: owner type aur owner dropdown se select karo."}
          </Alert>

          <Alert severity="info">
            Owner Type ka matlab:
            {" "}
            `Warehouse` = central stock,
            {" "}
            `Employee / Staff` = team member,
            {" "}
            `Customer / User` = end customer,
            {" "}
            `Vehicle` = vehicle installation.
            {isElectronicsCategory(item?.category) ? " Is category ke liye vehicle assignment recommended hai." : ""}
          </Alert>

          {isSerialized ? (
            <>
              <TextField
                select
                label={isTransfer ? "Assigned Serial/IMEI *" : "Available Serial/IMEI *"}
                value={serial}
                onChange={(e) => setSerial(e.target.value)}
                helperText={
                  unitsLoading
                    ? "Loading units..."
                    : availableUnits.length
                      ? `${availableUnits.length} unit(s) available`
                      : isTransfer
                        ? "No assigned unit available to transfer"
                        : "No in-stock unit available to assign"
                }
              >
                <MenuItem value="">-- Select --</MenuItem>
                {availableUnits.map((unit) => {
                  const value = unit?.imei || unit?.serial || unit?._id;
                  const label = unit?.imei
                    ? `${unit.imei}${unit?.serial && unit.serial !== unit.imei ? ` • ${unit.serial}` : ""}`
                    : unit?.serial || "Unknown unit";
                  return (
                    <MenuItem key={unit._id} value={value}>
                      {label}
                    </MenuItem>
                  );
                })}
              </TextField>
            </>
          ) : (
            <TextField
              type="number"
              label="Qty *"
              value={qty}
              onChange={(e) => setQty(e.target.value)}
              inputProps={{ min: 1 }}
              helperText={`Available stock: ${bulkInStock}`}
            />
          )}

          <TextField
            select
            label={isTransfer ? "To Owner Type" : "Owner Type"}
            value={ownerType}
            onChange={(e) => {
              setOwnerType(e.target.value);
              setSelectedOwner(null);
            }}
            helperText={selectedOwnerTypeMeta?.description || ""}
          >
            {ownerTypeOptions.map((option) => <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>)}
          </TextField>

          {ownerType !== "warehouse" && (
            <TextField
              select
              label={ownerType === "vehicle" ? "Select Vehicle *" : "Select Owner *"}
              value={selectedOwner?._id || ""}
              onChange={(e) => {
                const found = owners.find(x => String(x._id) === String(e.target.value));
                setSelectedOwner(found || null);
              }}
              helperText={
                isFetching
                  ? "Loading names..."
                  : owners.length
                    ? `${owners.length} option(s) available`
                    : "No option available"
              }
            >
              <MenuItem value="">-- Select --</MenuItem>
              {owners.map(o => (
                <MenuItem key={o._id} value={o._id}>{o.label}</MenuItem>
              ))}
            </TextField>
          )}

          {selectedOwner && (
            <TextField
              fullWidth
              label="Selected"
              value={selectedOwner.label}
              InputProps={{ readOnly: true }}
            />
          )}

          {!isSerialized && (
            <Alert severity={isTransfer ? "warning" : "info"}>
              {isTransfer
                ? "Bulk transfer direct supported nahi hai. Pehle return to warehouse karo, phir next owner ko assign karo."
                : "Bulk flow active. Qty-based assign use hoga."}
            </Alert>
          )}
        </Stack>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} disabled={busy}>Cancel</Button>
        <Button variant="contained" onClick={submit} disabled={submitDisabled}>
          {busy ? <CircularProgress size={18} /> : (isTransfer ? "Transfer" : "Assign")}
        </Button>
      </DialogActions>
    </Dialog>
  );
}


function ReturnDialogWrapper({ open, item, busy, onClose, onSubmit }) {
  const [serial, setSerial] = useState("");
  const [qty, setQty] = useState(1);
  const { data: unitsData, isLoading: unitsLoading } = useListItemUnitsQuery(
    { id: item?._id, status: "assigned" },
    { skip: !open || !item?._id || !item?.isSerialized }
  );
  const assignedUnits = Array.isArray(unitsData?.units) ? unitsData.units : [];

  useEffect(() => {
    if (!open) return;
    setSerial("");
    setQty(1);
  }, [open]);

  const submit = () => {
    if (item?.isSerialized) {
      if (!serial.trim()) return;
      onSubmit({ serial: serial.trim() });
    } else {
      onSubmit({ qty: Math.max(1, Number(qty || 1)) });
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth>
      <DialogTitle>Return to Warehouse: {item?.name || "-"}</DialogTitle>
      <DialogContent dividers>
        {item?.isSerialized ? (
          <>
            <TextField
              fullWidth
              select
              label="Assigned Serial/IMEI"
              value={serial}
              onChange={(e) => setSerial(e.target.value)}
              helperText={unitsLoading ? "Loading assigned units..." : `${assignedUnits.length} assigned unit(s) found`}
              sx={{ mb: 2 }}
            >
              <MenuItem value="">-- Select --</MenuItem>
              {assignedUnits.map((unit) => {
                const value = unit?.imei || unit?.serial || unit?._id;
                const label = unit?.imei
                  ? `${unit.imei}${unit?.serial && unit.serial !== unit.imei ? ` • ${unit.serial}` : ""}`
                  : unit?.serial || "Unknown unit";
                return (
                  <MenuItem key={unit._id} value={value}>
                    {label}
                  </MenuItem>
                );
              })}
            </TextField>
            <TextField fullWidth label="Or type Serial/IMEI manually" value={serial} onChange={(e) => setSerial(e.target.value)} />
          </>
        ) : (
          <TextField fullWidth type="number" label="Qty" value={qty} onChange={(e) => setQty(e.target.value)} helperText="Assigned bulk qty me se itna stock warehouse me return hoga" />
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={busy}>Cancel</Button>
        <Button variant="contained" onClick={submit} disabled={busy}>
          {busy ? <CircularProgress size={18} /> : "Return"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function ConfirmDeleteDialog({ open, item, busy, onClose, onConfirm }) {
  return (
    <BrandConfirmDialog
      open={open}
      onClose={onClose}
      onConfirm={onConfirm}
      title="Delete Item"
      message={`Delete ${item?.name ? `"${item.name}"` : "this item"}?`}
      description="This item will be soft deleted. This action cannot be undone from this screen."
      confirmLabel={busy ? "Deleting..." : "Yes, Delete"}
      tone="red"
      loading={busy}
    />
  );
}
